import { useSyncExternalStore } from 'react';

/*
 * Which layout the app uses, decided once here and written on <html> as data-layout (phone | tablet | desktop)
 * and data-orientation (portrait | landscape). The CSS reads these attributes (see "layouts" at the top of
 * styles/app.css) instead of repeating width/height media queries, because a height-based media query is wrong
 * while typing: on Android (interactive-widget=resizes-content) and sometimes in iOS home-screen apps the page
 * itself gets shorter when the keyboard opens, and a tablet held sideways would flip to the phone layout as soon
 * as a field got focus. Here the height is the one measured while nothing is being typed into.
 *
 *  phone    width <= 720, or a touch screen whose height is <= 500 (a phone held sideways): one pane at a time,
 *           the bottom tab bar.
 *  tablet   721..1024 wide: rail, list and conversation side by side, a narrower rail and list.
 *  desktop  wider than 1024.
 */
export type Layout = 'phone' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

export const PHONE_MAX_WIDTH = 720;
export const PHONE_MAX_HEIGHT = 500;
export const TABLET_MAX_WIDTH = 1024;

export interface LayoutInput {
  width: number;
  /** The visible height with no on-screen keyboard. */
  height: number;
  /** The main pointer is a finger (pointer: coarse). */
  touch: boolean;
}

export interface LayoutState {
  layout: Layout;
  orientation: Orientation;
}

export function decideLayout({ width, height, touch }: LayoutInput): LayoutState {
  const orientation: Orientation = width > height ? 'landscape' : 'portrait';
  if (width <= PHONE_MAX_WIDTH || (touch && height <= PHONE_MAX_HEIGHT)) return { layout: 'phone', orientation };
  if (width <= TABLET_MAX_WIDTH) return { layout: 'tablet', orientation };
  return { layout: 'desktop', orientation };
}

function typing(): boolean {
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color'].includes(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

const touchScreen = () => typeof window.matchMedia === 'function' && (window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none) and (any-pointer: coarse)').matches);

let current: LayoutState = { layout: 'desktop', orientation: 'landscape' };
const listeners = new Set<() => void>();

/** Starts following the window; call once before the first render (main.tsx). */
export function trackLayout(): void {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  let width = -1;
  let height = 0;
  const update = () => {
    const w = window.innerWidth;
    // While a field has focus a shorter height may be the keyboard's doing: keep the one from before, unless the
    // width changed (turned round, a new window size), when there is nothing better to go on. Taller is never
    // the keyboard.
    if (w !== width || !typing() || window.innerHeight > height) height = window.innerHeight;
    width = w;
    const next = decideLayout({ width: w, height, touch: touchScreen() });
    if (next.layout === current.layout && next.orientation === current.orientation && root.dataset.layout) return;
    current = next;
    root.dataset.layout = next.layout;
    root.dataset.orientation = next.orientation;
    listeners.forEach((fn) => fn());
  };
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  // The keyboard going away leaves the true height behind.
  window.addEventListener('focusout', () => setTimeout(update, 200));
  if (typeof window.matchMedia === 'function') window.matchMedia('(pointer: coarse)').addEventListener?.('change', update);
  update();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The layout in use (phone | tablet | desktop), following changes. */
export function useLayout(): Layout {
  return useSyncExternalStore(subscribe, () => current.layout, () => 'desktop');
}
